import WSService from "../../web-socket/web-socket-service";
import { api } from "../api/callApi";
import { urlServer } from "../api/v1";
import { DISEASED_ZONE, OPERATION_TYPE, PriorityTime, PROCESS_TYPE } from "../varibles/constants";
import {
    IActivityLog,
    IDeliveryTypeUnit,
    IHarvest,
    IHarvest2,
    IOperation,
    IOperation2,
    IPois,
    IPosition,
    IRealTreatmentUnit2,
    IRoundPlan,
    ITankProcessData,
    IUnit,
    IVessel,
    IWeatherData,
    IWellboatsResponse,
    TOperation2,
    TTaskTreatment
} from "../varibles/interface";

const urlBase = urlServer + '/operation'

interface IOperationConfirm {
    delete_operations: string[]
    vessels: {
        vessel_id: string
        operations: any[]
    }
}

interface IUpdateOperationStatus {
    operation_id: string,
    tenant_id: string,
    status: string
}

interface IParamGetOperationWeather {
    operation_type: OPERATION_TYPE
    source_id?: string
    destination_id?: string
    pois: IPois[]
    activity_log: {
        est_start_time: number
    }[]
}

interface IGetOpsForDelete {
    operation_ids: string[]
    vessels: {
        vessel_id: string
        vessel_owner_id: string
    }[],
    start_time: number
    finish_time: number
}

interface IAddDelayTime {
    operation_id: string
    tenant_id: string
    time_late: number
    step: number
}

interface IGetActivityLogParams {
    is_weather: boolean
    is_activity_log: boolean

    vessel_id: string
    router_id: string

    priority: number
    est_start_time: number
    operation_type: number
    total_amount: number
    total_weight: number

    start_place: {
        id: string
        field: string
        latitude: number
        longitude: number
    },
    source: {
        id: string
        time: number
        latitude: number
        longitude: number
    },
    destination: {
        id: string
        time: number
        latitude: number
        longitude: number
    }
}

export interface IGetTanks {
    vessel_id: string
    units: {
        id: string
        ave_weight: number
        total_weight: number
        tanks: any[],
        process_type: PROCESS_TYPE
        density_percent?: number
    }[]
    after_round?: {
        [round_id: string]: any
    }
    start_time: number
}

export interface IHarvestTanks {
    site_arrival_time: number
    priority: PriorityTime
    factory_time: number
    vessel_id: string
    router_id?: string
    source_id: string
    destination_id: string
    start_time: number
    is_journey_to_site?: boolean
    is_cleaning?: boolean
    sub_operations: {
        id: string
        unit_id?: string
        avg_weight?: number
        fish_amount?: number
        total_weight?: number
        delivery_types?: IDeliveryTypeUnit[]
    }[]
}

interface IParamsAutoPan {
    vessels: string[],
    harvests: {
        harvest_id: string,
        site_id: string,
        factory_id: string,
        unit_id: string,
        avg_weight: number,
        fish_amount: number,
        packing_date: number
    }[]
}

interface IResultActivityLog {
    activity_log: IActivityLog[]
    router_id: string
    prev_op_router_id: string
    support_vessels: any[]
    tasks: TTaskTreatment[]
}

interface IUpdateActivityLog {
    router_id: string
    activity_log: IActivityLog[]
}

interface IPauseParams {
    operation_id: string;
    tenant_id: string;
    time: number;
    reason?: string;
}

export const OpService = {
    detail: (tenant_id: any, id: any) => api.get<IOperation>(urlBase + '/' + tenant_id + '/' + id),
    autoPlan: (body: IParamsAutoPan) => api.post<{ operations: IOperation2[], harvests: { [id: string]: any } }>(urlBase + '/plan', body),
    getCanceled: () => api.post<{ operations: any }>(urlBase + '/cancel'),
    activityLog: (body: IGetActivityLogParams, abort?: AbortController) => api.post<IResultActivityLog>(urlBase + '/activity_log', body, { signal: abort?.signal }),
    tanks: (body: IGetTanks, abort?: AbortController) => api.post<{
        round_map: { [round_id: string]: IRoundPlan }
        duration: number
    }>(urlBase + '/tank', body, { signal: abort?.signal }),
    harvestTanks: (body: IHarvestTanks, abort?: AbortController) => api.post<{
        activity_log: IActivityLog[]
        sub_operations: IUnit[]
        tanks: IHarvest['tanks']
        router_id: string
    }>(urlBase + '/tank/harvest', body, { signal: abort?.signal }),
    addDelay: (body: IAddDelayTime) => {
        const connection_id = WSService.getConnectId();
        return api.put(urlBase + '/update/ongoing', { ...body, connection_id })
    },
    getOpsForDelete: (body: IGetOpsForDelete) => {
        const connection_id = WSService.getConnectId();
        return api.post(urlBase + '/delete', { ...body, connection_id })
    },
    clearOps: (body: any, abort?: AbortController) => api.post(urlBase + '/ids', body, { signal: abort?.signal }),
    updateActivityLog: (body: IUpdateActivityLog, id: string) => api.put(urlBase + "/" + id, body),
    starvings: () => api.get(urlBase + "/starving"),
    getWeather: (body: IParamGetOperationWeather) => api.post<{ weather: any, typical_condition: number, real_sub_operations?: IRealTreatmentUnit2[], summary?: IHarvest2['summary'] }>(urlBase + '/weather', body),
    getWeatherLive: (body: { id: string, tenant_id: string }) => api.post<{ weather: IWeatherData, typical_condition: number }>(urlBase + "/weather/live", body),
    updateStatus: (body: IUpdateOperationStatus) => {
        const connection_id = WSService.getConnectId();
        return api.put(urlBase + '/update', { ...body, connection_id })
    },
    getOpRelated: (body: { related_id: string, tenant_id: string }) => api.post<IResultGetRelated[]>(urlBase + '/related', body),
    delete: (opId: string) => {
        const connection_id = WSService.getConnectId();
        return api.deleteBody<{ deleted_operations: string[] }>(urlBase + `/${opId}`, { connection_id })
    },
    updateNote: (params: { note: string }, operationId: string) => api.put(urlBase + '/note/' + operationId, params),
    updateStartPoint: (body: {
        id: string
        tenant_id: string
        start_point: IVessel['start_point']
    }) => api.post<NonNullable<TOperation2['billings']>>(urlBase + '/start-point', body),
    actualRoute: (body: { id: string, tenant_id: string }) => api.post<{ real_route: { points: (IPosition & { time: string })[] }, route_diseases: { type: DISEASED_ZONE, forskNr: string }[] }>(urlBase + '/route', body),
    getScheduleOfWeeks2: (start_time: number, finish_time: number, abort?: AbortController) => api.post(urlBase + '/history', { start_time, finish_time }, { signal: abort?.signal }),
    getSensorData: (body: { id: string, tenant_id: string, start_time: number, finish_time: number }) => api.post<IResultSensorData>(urlBase + '/sensor-data', body),
    createOps: (body: IOperationConfirm) => {
        const connection_id = WSService.getConnectId();
        return api.post(urlBase + '/confirm', { ...body, connection_id })
    },
    saveOps: (body: IOperationConfirm) => {
        const connection_id = WSService.getConnectId();
        return api.put(urlBase + '/confirm', { ...body, connection_id })
    },
    getScheduleOfWeeks: (start_time: number, finish_time: number) => api.put(urlBase + '/history', { start_time, finish_time }),
    getDashboardOperations: () => api.get(urlBase + '/dashboard'),
    pause: (body: IPauseParams) => {
        return api.put<{ operations: IOperation2[] }>(urlBase + '/pause', body)
    },
    getWellboatsForService: (start_time: number, finish_time: number, abort?: AbortController) => api.put<IWellboatsResponse>(urlBase + '/history/vessels', { start_time, finish_time }, { signal: abort?.signal }),
    updateInvoiceRelated: (body: { invoices: IOperation2['operation']['invoices'] }) => api.post(urlBase + '/link-billing', body),
}

export interface IResultGetRelated { est_start_time: number, est_finish_time: number, vessel_id?: string, vessel_name: string, id: string, operation_code: string, operation_type: OPERATION_TYPE }

export interface IResultSensorData {
    total_fuel: number;
    total_diesel: number;

    [tankName: string]: ITankProcessData | number;
}
